<template>
  <div class="wrraper payment_sessions_page">
    <!-- Start Breadcrumb -->
    <Breadcrumb :items="items" />
    <!-- End Breadcrumb -->

    <!-- Start 403 State -->
    <div v-if="forbidden" class="forbidden_state text-center">
      <i class="fal fa-lock mb-3"></i>
      <p>{{ forbiddenMessage || $t("paymentSessions.forbidden") }}</p>
    </div>
    <!-- End 403 State -->

    <template v-else>
      <div class="card-header mb-4">
        <h4 class="card-title">
          <i class="fas fa-credit-card ml-2"></i>
          {{ $t("paymentSessions.title") }}
          <span class="total">({{ meta.total }})</span>
        </h4>
      </div>

      <!-- Start Filters -->
      <div class="d-flex align-items-center filters-row">
        <the-filter
          class="flex-grow-1"
          :customName2="$t('paymentSessions.filters.sessionType')"
          :inputs="inputs"
          :types="sessionTypeItems"
        />
        <button
          type="button"
          class="reset-btn flex-shrink-0"
          :title="$t('paymentSessions.filters.reset')"
          @click="resetFilters"
        >
          <i class="fal fa-undo"></i>
        </button>
      </div>
      <!-- End Filters -->

      <!-- Start Table (read only, no create / edit / delete) -->
      <main class="magic-table-wrapper">
        <v-data-table
          class="thumb strip"
          :headers="headers"
          :items="rows"
          :loading="loading"
          :loading-text="$t('table.loadingData')"
          item-key="id"
          :items-per-page="-1"
          disable-sort
          hide-default-footer
        >
          <template v-slot:[`item.session_type`]="{ item }">
            {{ sessionTypeLabel(item.session_type) }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <span class="status_label">{{ dash(item.status) }}</span>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span dir="ltr">{{ dash(item.amount) }}</span>
          </template>

          <template v-slot:[`item.webhook_data`]="{ item }">
            <v-btn
              v-if="hasValue(item.webhook_data)"
              small
              outlined
              color="primary"
              @click="openWebhook(item)"
            >
              {{ $t("paymentSessions.webhook.view") }}
            </v-btn>
            <span v-else>-</span>
          </template>

          <template v-for="key in textColumns" v-slot:[`item.${key}`]="{ item }">
            <span :key="key" class="cell_text">{{ dash(item[key]) }}</span>
          </template>

          <template v-for="key in dateColumns" v-slot:[`item.${key}`]="{ item }">
            <span :key="key" dir="ltr">{{ dash(item[key]) }}</span>
          </template>

          <template v-slot:no-data>
            {{ $t("table.noData") }}
          </template>
        </v-data-table>
      </main>
      <!-- End Table -->

      <!-- Start Pagination -->
      <div
        class="pagination_container text-center my-4 d-flex justify-content-end"
      >
        <div class="select-pagination d-flex">
          <span class="first">{{ $t("show") }}</span>
          <v-select
            :items="perPageOptions"
            v-model="perPage"
            @change="onPerPageChange"
          ></v-select>
          <span>{{ $t("entries") }}</span>
        </div>

        <v-pagination
          v-if="meta.last_page > 1"
          v-model.number="page"
          :length="meta.last_page"
          :total-visible="5"
          :disabled="loading"
          @input="fetchData"
        ></v-pagination>
      </div>
      <!-- End Pagination -->

      <!-- Start Webhook Dialog -->
      <v-dialog v-model="webhookDialog" max-width="700px">
        <v-card class="p-4">
          <h5 class="mb-3">{{ $t("paymentSessions.webhook.title") }}</h5>
          <pre class="webhook_data" dir="ltr">{{ webhookText }}</pre>
        </v-card>
      </v-dialog>
      <!-- End Webhook Dialog -->
    </template>
  </div>
</template>

<script>
import TheFilter from "@/components/Filters/TheFilter.vue";

// Plain text / number filters: route query key => api param
const TEXT_FILTERS = [
  { key: "gateway", param: "gateway", label: "gateway", col: "3" },
  { key: "session_status", param: "status", label: "status", col: "3" },
  { key: "user_id", param: "user_id", label: "userId", col: "3" },
  { key: "order_id", param: "order_id", label: "orderId", col: "3" },
  {
    key: "cash_out_request_id",
    param: "cash_out_request_id",
    label: "cashOutRequestId",
    col: "3",
  },
  {
    key: "transaction_id",
    param: "transaction_id",
    label: "transactionId",
    col: "3",
  },
  {
    key: "special_reference",
    param: "special_reference",
    label: "specialReference",
    col: "3",
  },
  {
    key: "idempotency_key",
    param: "idempotency_key",
    label: "idempotencyKey",
    col: "3",
  },
];

export default {
  name: "PaymentSessions",

  components: { TheFilter },

  data() {
    return {
      // ========== Breadcrumbs
      items: [
        {
          text: this.$t("breadcrumb.mainPage"),
          disabled: false,
          href: "/",
        },
        {
          text: this.$t("breadcrumb.payment_sessions.title"),
          disabled: true,
          href: "",
        },
      ],

      sessionTypes: ["payment", "payout"],
      perPageOptions: [15, 30, 50, 100],
      perPage: 15,

      textColumns: [
        "user_id",
        "order_id",
        "cash_out_request_id",
        "gateway",
        "special_reference",
        "transaction_id",
        "idempotency_key",
        "currency",
        "attempt_count",
      ],
      dateColumns: ["last_attempt_at", "completed_at", "created_at", "updated_at"],

      rows: [],
      page: 1,
      meta: { current_page: 1, last_page: 1, per_page: 15, total: 0 },

      webhookDialog: false,
      webhookText: "",

      loading: false,
      forbidden: false,
      forbiddenMessage: "",
      // Ignore a slow response when a newer request was already sent
      requestId: 0,
    };
  },

  computed: {
    sessionTypeItems() {
      return this.sessionTypes.map((id) => ({
        id,
        name: this.$t(`paymentSessions.sessionTypes.${id}`),
      }));
    },

    // Same filter component as the other pages, driven by the route query
    inputs() {
      return [
        { keyName: "type", col: "3" },
        ...TEXT_FILTERS.map((f) => ({
          keyName: f.key,
          text: true,
          col: f.col,
          label: this.$t(`paymentSessions.filters.${f.label}`),
        })),
        { keyName: "start_date", col: "3" },
        { keyName: "end_date", col: "3" },
      ];
    },

    headers() {
      const col = (key) => this.$t(`paymentSessions.columns.${key}`);
      return [
        ["id", "id"],
        ["user_id", "userId"],
        ["order_id", "orderId"],
        ["cash_out_request_id", "cashOutRequestId"],
        ["gateway", "gateway"],
        ["session_type", "sessionType"],
        ["special_reference", "specialReference"],
        ["transaction_id", "transactionId"],
        ["idempotency_key", "idempotencyKey"],
        ["amount", "amount"],
        ["currency", "currency"],
        ["status", "status"],
        ["webhook_data", "webhookData"],
        ["attempt_count", "attemptCount"],
        ["last_attempt_at", "lastAttemptAt"],
        ["completed_at", "completedAt"],
        ["created_at", "createdAt"],
        ["updated_at", "updatedAt"],
      ].map(([value, label]) => ({
        text: col(label),
        value,
        align: "center",
      }));
    },
  },

  watch: {
    // Every filter / page change made in the-filter lands in the route query
    "$route.query"() {
      this.page = +this.$route.query.page || 1;
      this.getSessions();
    },
  },

  methods: {
    sessionTypeLabel(value) {
      return this.sessionTypes.includes(value)
        ? this.$t(`paymentSessions.sessionTypes.${value}`)
        : this.dash(value);
    },

    dash(value) {
      return value === null || value === undefined || value === ""
        ? "-"
        : value;
    },

    hasValue(value) {
      if (value === null || value === undefined || value === "") return false;
      return typeof value === "object" ? Object.keys(value).length > 0 : true;
    },

    // `webhook_data` can come back as an object or as a json string
    openWebhook(item) {
      let data = item.webhook_data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          // keep the raw string
        }
      }
      this.webhookText =
        typeof data === "string" ? data : JSON.stringify(data, null, 2);
      this.webhookDialog = true;
    },

    // Only send the filters the user actually filled
    buildParams() {
      const q = this.$route.query;
      const params = { per_page: this.perPage, page: this.page };
      if (q.type) params.session_type = q.type;
      TEXT_FILTERS.forEach((f) => {
        if (q[f.key]) params[f.param] = q[f.key];
      });
      if (q.start_date) params.from = q.start_date;
      if (q.end_date) params.to = q.end_date;
      return params;
    },

    resetFilters() {
      this.$router.push({ query: { page: 1 } }).catch(() => {});
    },

    fetchData(page) {
      this.$router
        .push({ query: { ...this.$route.query, page } })
        .catch(() => {});
    },

    onPerPageChange() {
      this.page = 1;
      if (+this.$route.query.page > 1) {
        // The route watcher reloads the list
        this.fetchData(1);
      } else {
        this.getSessions();
      }
    },

    getSessions() {
      const currentRequest = ++this.requestId;
      this.loading = true;

      this.$axios({
        method: "GET",
        url: "payment_sessions",
        params: this.buildParams(),
      })
        .then((res) => {
          if (currentRequest !== this.requestId) return;
          const body = res.data || {};
          this.rows = Array.isArray(body.data) ? body.data : [];
          this.meta = { ...this.meta, ...(body.meta || {}) };
          this.loading = false;
        })
        .catch((err) => {
          if (currentRequest !== this.requestId) return;
          // The spinner never stays after 403 / 422
          this.loading = false;
          const status = err.response?.status;
          const message = err.response?.data?.message;

          if (status === 403) {
            this.forbidden = true;
            this.forbiddenMessage = message || "";
            return;
          }
          // 401 is handled by the global axios interceptor (admin logout)
          if (status === 401) return;

          // 422 shows the first message, the screen and filters stay
          this.$iziToast.error({
            timeout: 3000,
            message: this.firstError(err) || this.$t("globalErr"),
            position: "bottomRight",
          });
        });
    },

    firstError(err) {
      const data = err.response?.data;
      if (data?.errors) {
        const first = Object.values(data.errors)[0];
        if (Array.isArray(first) && first.length) return first[0];
      }
      return data?.message || "";
    },
  },

  created() {
    this.page = +this.$route.query.page || 1;
    this.getSessions();
  },
};
</script>

<style lang="scss" scoped>
.payment_sessions_page {
  .card-header {
    background: transparent;
    border: none;
    padding: 0;

    .card-title {
      font-weight: bold;
      margin-bottom: 4px;

      .total {
        font-size: 14px;
        color: #8a8a8a;
      }
    }
  }

  .filters-row {
    column-gap: 10px;

    .reset-btn {
      width: 46px;
      height: 46px;
      flex: 0 0 46px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: 1px solid var(--mainColor-2);
      border-radius: 50%;
      background-color: transparent;
      color: var(--mainColor-2);
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover,
      &:focus {
        background-color: var(--mainColor-2);
        color: #fff;
        outline: none;
      }
    }
  }

  .forbidden_state {
    padding: 60px 0;

    i {
      font-size: 5rem;
      color: #8a8a8a;
    }
  }

  .cell_text {
    display: inline-block;
    max-width: 220px;
    word-break: break-all;
  }

  .status_label {
    display: inline-block;
    padding: 2px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: bold;
    color: var(--mainColor-2);
    background: rgba(120, 61, 115, 0.12);
  }

  .webhook_data {
    max-height: 60vh;
    overflow: auto;
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.04);
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
  }
}
</style>
